/**
 * Eine Tabelle, in der jede Spalte ihren eigenen Filter hat.
 *
 * Warum je Spalte und nicht ein Suchfeld über alles: Ein Suchfeld beantwortet
 * „wo kommt Moosbeuren vor". Die Frage im Büro lautet aber „welche Touren von
 * Martin am Donnerstag sind noch offen" — drei Einschränkungen gleichzeitig,
 * jede in ihrer eigenen Spalte. Ein einzelnes Suchfeld kann das nicht.
 *
 * Spalten mit wenigen wiederkehrenden Werten (Kunde, Fahrer, Zustand) bekommen
 * eine Auswahl statt eines Textfelds. Die Werte stammen aus den Daten selbst —
 * eine gepflegte Liste möglicher Filterwerte wäre eine zweite Wahrheit, die
 * beim ersten neuen Fahrer falsch wird.
 *
 * Sortiert wird per Klick auf die Überschrift. Freitextfilter suchen
 * fehlertolerant (daten/textvergleich.ts), damit „hersch" auch „Werk
 * Herrschaftsholz" findet.
 *
 * ═══ SORTIERT WIRD DER WERT, NICHT DER TEXT ═══
 *
 * Gefiltert wird nach dem, was man sieht und tippt (`wert`) — „05.10" soll
 * den 5. Oktober finden. Sortiert wird nach `sortierwert`. Vorher war beides
 * derselbe Text, und das Ladedatum „Mo., 24.08.26" sortierte nach dem
 * Wochentag (H1.5). Jede Spalte, deren Anzeige nicht alphabetisch sortiert —
 * Datum, Zeitpunkt, Betrag —, gibt deshalb einen eigenen Sortierwert an.
 * Zeilen ohne Wert stehen in beide Richtungen hinten (daten/sortierung.ts).
 *
 * ═══ JEDE ZEILE IST GLEICH HOCH ═══
 *
 * Keine Zelle bricht um. Vorher wurde eine Zeile mit langem Ortsnamen doppelt
 * so hoch wie ihre Nachbarn, und über achtzig Touren hinweg entstand daraus
 * eine Treppe, in der man beim Überfliegen die Zeile verliert, der man gerade
 * folgt. Der Preis dafür ist eine Tabelle, die breiter sein darf als der
 * Bildschirm und waagerecht scrollt — das ist die bewusste Wahl: lieber
 * schieben als suchen. Was nicht in die Breite passt, steht im Tooltip.
 *
 * ═══ DIE LEISTE, DER KOPF UND DIE ERSTE SPALTE BLEIBEN STEHEN (HJ-041) ═══
 *
 * Je mehr Touren, desto mühsamer: Die Leiste zum Seitwärts-Scrollen saß unter
 * der letzten Zeile, Kopf und Filter scrollten weg, und nach dem Schieben nach
 * rechts war nicht mehr zu sehen, zu welcher Tour die Zeile gehört. Der Rahmen
 * endet jetzt am Fensterende (`rahmenhoehe.ts`) und scrollt in beide Richtungen
 * selbst; das Stehenbleiben macht das CSS (`.tabellenrahmen--fenster`). Gemessen
 * wird neu, wenn sich die Seite ändert — darüber gehen Formular und Rückfrage auf
 * und zu. Das sieht der Beobachter am `body`, das Fenster meldet es nicht.
 */

import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { passt } from '../daten/textvergleich';
import {
  sortierwerteVergleichen,
  zahlAusText,
  type Sortierwert,
} from '../daten/sortierung';
import { rahmenhoehe } from './rahmenhoehe';

export type Spalte<T> = {
  schluessel: string;
  text: string;
  /**
   * Der Text, nach dem gefiltert wird — so, wie er in der Zelle steht. Ohne
   * `sortierwert` wird auch danach sortiert, alphabetisch.
   */
  wert: (zeile: T) => string;
  /**
   * Wonach sortiert wird, wenn die Anzeige nicht alphabetisch sortiert:
   * Datum als `JJJJ-MM-TT`, Zeitpunkt oder Betrag als Zahl. `null` = kein
   * Wert, steht immer hinten. Ein Datum OHNE Sortierwert sortiert nach dem
   * Wochentag — genau das war H1.5.
   */
  sortierwert?: (zeile: T) => Sortierwert;
  /** Was in der Zelle steht. Ohne Angabe der Wert selbst. */
  zeige?: (zeile: T) => ReactNode;
  /** Auswahlliste statt Freitext — für Spalten mit wenigen Werten. */
  auswahl?: boolean;
  rechts?: boolean;
  /**
   * Für Zahlen, deren `wert` die Zahl als Text liefert: numerisch sortieren
   * statt alphabetisch. Ein leerer Text ist dabei KEIN Wert, keine 0.
   */
  zahl?: boolean;
  /**
   * Nicht auf Höchstbreite kürzen.
   *
   * Für Spalten, die Knöpfe enthalten: Ein gedeckelter Kasten zwingt drei
   * Knöpfe nebeneinander in drei Zeilen untereinander — und dann ist die Zeile
   * dreimal so hoch wie ihre Nachbarn. Genau das sollte die Deckelung
   * verhindern.
   */
  nichtKuerzen?: boolean;
};

export function Filtertabelle<T>(eigenschaften: {
  spalten: Spalte<T>[];
  zeilen: T[];
  schluessel: (zeile: T) => string;
  /** Zeile anklickbar — führt meist in eine Detailsicht. */
  onZeile?: (zeile: T) => void;
  leer: ReactNode;
  /** Wird über der Tabelle angezeigt: „12 von 87". */
  zaehlung?: (gezeigt: number, gesamt: number) => ReactNode;
  /** Die Reihenfolge, bevor jemand auf eine Überschrift klickt. */
  anfangssortierung?: { schluessel: string; ab: boolean };
}) {
  const [filter, setFilter] = useState<Record<string, string>>({});
  const [sortierung, setSortierung] = useState<{
    schluessel: string;
    ab: boolean;
  } | null>(eigenschaften.anfangssortierung ?? null);

  const rahmen = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = rahmen.current;
    if (!element) return;
    const messen = () => {
      const oberkante = element.getBoundingClientRect().top + window.scrollY;
      element.style.maxHeight = `${rahmenhoehe(window.innerHeight, oberkante)}px`;
    };
    messen();
    window.addEventListener('resize', messen);
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(document.body);
    return () => {
      window.removeEventListener('resize', messen);
      beobachter.disconnect();
    };
  }, []);

  const werte = useMemo(() => {
    const gesammelt: Record<string, string[]> = {};
    for (const spalte of eigenschaften.spalten) {
      if (!spalte.auswahl) continue;
      const einzelne = new Set<string>();
      for (const zeile of eigenschaften.zeilen) {
        const wert = spalte.wert(zeile).trim();
        if (wert) einzelne.add(wert);
      }
      gesammelt[spalte.schluessel] = [...einzelne].sort((a, b) =>
        a.localeCompare(b, 'de'),
      );
    }
    return gesammelt;
  }, [eigenschaften.spalten, eigenschaften.zeilen]);

  const gefiltert = useMemo(() => {
    let liste = eigenschaften.zeilen.filter((zeile) =>
      eigenschaften.spalten.every((spalte) => {
        const gesucht = filter[spalte.schluessel];
        if (!gesucht) return true;
        const wert = spalte.wert(zeile);
        // Eine Auswahl meint genau diesen Wert. Freitext darf danebenliegen.
        return spalte.auswahl ? wert === gesucht : passt(wert, gesucht);
      }),
    );

    if (sortierung) {
      const spalte = eigenschaften.spalten.find(
        (s) => s.schluessel === sortierung.schluessel,
      );
      if (spalte) {
        const sortierwert = (zeile: T): Sortierwert =>
          spalte.sortierwert
            ? spalte.sortierwert(zeile)
            : spalte.zahl
              ? zahlAusText(spalte.wert(zeile))
              : spalte.wert(zeile);
        liste = [...liste].sort((a, b) =>
          sortierwerteVergleichen(sortierwert(a), sortierwert(b), sortierung.ab),
        );
      }
    }
    return liste;
  }, [eigenschaften.zeilen, eigenschaften.spalten, filter, sortierung]);

  const aktiveFilter = Object.values(filter).filter(Boolean).length;

  function umschalten(schluessel: string) {
    setSortierung((bisher) =>
      bisher?.schluessel === schluessel
        ? { schluessel, ab: !bisher.ab }
        : { schluessel, ab: false },
    );
  }

  return (
    <>
      <div className="reihe" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <span className="meta">
          {eigenschaften.zaehlung
            ? eigenschaften.zaehlung(gefiltert.length, eigenschaften.zeilen.length)
            : `${gefiltert.length} von ${eigenschaften.zeilen.length}`}
        </span>
        {aktiveFilter > 0 && (
          <button className="knopf knopf--still knopf--klein" onClick={() => setFilter({})}>
            {aktiveFilter === 1 ? 'Filter zurücksetzen' : `${aktiveFilter} Filter zurücksetzen`}
          </button>
        )}
      </div>

      <div className="tabellenrahmen tabellenrahmen--fenster" ref={rahmen}>
        <table className="tabelle tabelle--filter">
          <thead>
            <tr>
              {eigenschaften.spalten.map((spalte) => (
                <th
                  key={spalte.schluessel}
                  className={spalte.rechts ? 'tabelle__rechts' : undefined}
                >
                  <button
                    className="sortknopf"
                    onClick={() => umschalten(spalte.schluessel)}
                    title="Sortieren"
                  >
                    {spalte.text}
                    {sortierung?.schluessel === spalte.schluessel && (
                      <span aria-hidden="true">{sortierung.ab ? ' ↓' : ' ↑'}</span>
                    )}
                  </button>
                </th>
              ))}
            </tr>
            <tr className="filterzeile">
              {eigenschaften.spalten.map((spalte) => (
                <th key={spalte.schluessel}>
                  {spalte.auswahl ? (
                    <select
                      value={filter[spalte.schluessel] ?? ''}
                      onChange={(e) =>
                        setFilter((b) => ({
                          ...b,
                          [spalte.schluessel]: e.target.value,
                        }))
                      }
                    >
                      <option value="">alle</option>
                      {(werte[spalte.schluessel] ?? []).map((w) => (
                        <option key={w} value={w}>
                          {w}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="search"
                      value={filter[spalte.schluessel] ?? ''}
                      placeholder="filtern"
                      onChange={(e) =>
                        setFilter((b) => ({
                          ...b,
                          [spalte.schluessel]: e.target.value,
                        }))
                      }
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {gefiltert.length === 0 ? (
              <tr>
                <td
                  className="tabelle__leer"
                  colSpan={eigenschaften.spalten.length}
                >
                  {aktiveFilter > 0
                    ? 'Keine Zeile passt zu den Filtern.'
                    : eigenschaften.leer}
                </td>
              </tr>
            ) : (
              gefiltert.map((zeile) => (
                <tr
                  key={eigenschaften.schluessel(zeile)}
                  className={eigenschaften.onZeile ? 'anklickbar' : undefined}
                  onClick={() => eigenschaften.onZeile?.(zeile)}
                >
                  {eigenschaften.spalten.map((spalte) => (
                    <td
                      key={spalte.schluessel}
                      className={spalte.rechts ? 'tabelle__rechts' : undefined}
                    >
                      {/* Der Kasten innen hält die Zelle einzeilig und deckelt
                          ihre Breite. Der vollständige Wert steht im Tooltip —
                          er ist nicht verloren, nur zusammengeschoben.
                          Knopfspalten bleiben ungedeckelt, sonst brechen sie
                          um und machen die Zeile hoch. */}
                      {spalte.nichtKuerzen ? (
                        (spalte.zeige ? spalte.zeige(zeile) : spalte.wert(zeile))
                      ) : (
                        <span className="zelle" title={spalte.wert(zeile)}>
                          {spalte.zeige ? spalte.zeige(zeile) : spalte.wert(zeile)}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
